// AttendancePage.js
import React, { useEffect, useState } from 'react';
import { MapPin, Clock, CheckCircle, XCircle, AlertCircle, Calendar, Download, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { fetchPlatformSettings } from '../../services/platformSettings';
import './AdminPages.css';

export default function AttendancePage() {
  const { data, commitData, showToast } = useApp();
  const { can, isFounder, user, role } = useAuth();

  const canViewAll = can('canViewAllAttendance');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [geoSettings, setGeoSettings] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveSaving, setLeaveSaving] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    type: 'Annual Leave',
    from: '',
    to: '',
    reason: '',
  });

  const myLeaveRequests = (data.leaveRequests || []).filter(
    (l) => String(l.employeeId) === String(user?.employeeId)
  );

  const records = canViewAll
    ? data.attendance
    : data.attendance.filter(a => String(a.employeeId) === String(user?.employeeId));

  const present = records.filter(a=>a.status==='Present').length;
  const absent  = records.filter(a=>a.status==='Absent').length;
  const onLeave = records.filter(a=>a.status==='Leave').length;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await fetchPlatformSettings();
        if (!cancelled) setGeoSettings(s.geoSettings);
      } catch {
        // keep null; UI will show disabled state
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onUpdate = (e) => {
      const next = e?.detail?.geoSettings;
      if (!next) return;
      setGeoSettings(next);
    };
    window.addEventListener('fortitude:platformSettingsUpdated', onUpdate);
    return () => window.removeEventListener('fortitude:platformSettingsUpdated', onUpdate);
  }, []);

  function toNum(v) {
    const n = typeof v === 'string' ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : null;
  }

  function haversineMeters(lat1, lon1, lat2, lon2) {
    const R = 6371000; // earth radius in meters
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function formatHHMM(d) {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  async function getCurrentGeolocation() {
    if (!navigator.geolocation) throw new Error('Geolocation not supported by this browser.');
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 3000 }
      );
    });
  }

  const ensureTodayAttendanceRecord = (employeeId, employee, status, location) => {
    const recId = `ATT-${employeeId}-${date}`;
    const existing = (data.attendance || []).find(
      (a) => String(a.employeeId) === String(employeeId) && String(a.date) === String(date)
    );

    const base = existing || {
      id: recId,
      employeeId,
      employee,
      date,
      checkIn: '-',
      checkOut: '-',
      status,
      location,
    };

    return { existing, base };
  };

  const checkIn = async () => {
    if (!user?.employeeId) {
      showToast('Employee profile not found for this account', 'error');
      return;
    }

    const employeeId = user.employeeId;
    const employee = user.name || 'Employee';

    let location = 'Office';
    let remote = false;

    if (geoSettings?.enabled) {
      const officeLat = toNum(geoSettings.lat);
      const officeLng = toNum(geoSettings.lng);
      const radiusMeters = toNum(geoSettings.radius);
      if (officeLat == null || officeLng == null || radiusMeters == null) {
        showToast('Geo settings are missing or invalid; falling back to Office', 'warning');
      } else {
        try {
          const pos = await getCurrentGeolocation();
          const userLat = pos.coords.latitude;
          const userLng = pos.coords.longitude;
          const distance = haversineMeters(officeLat, officeLng, userLat, userLng);
          remote = distance > radiusMeters;
          location = remote ? 'Remote' : 'Office';
        } catch {
          showToast('Could not read your location; falling back to Office', 'warning');
        }
      }
    }

    const status = remote && geoSettings?.enforceOnRemote ? 'Absent' : 'Present';
    const { existing, base } = ensureTodayAttendanceRecord(employeeId, employee, status, location);
    const now = new Date();
    const nextAttendance = (data.attendance || []).slice();

    const record = {
      ...base,
      status,
      location,
      checkIn: base.checkIn !== '-' && existing ? base.checkIn : formatHHMM(now),
    };

    const updated = existing
      ? nextAttendance.map((a) => String(a.id) === String(existing.id) ? { ...a, ...record } : a)
      : nextAttendance.concat(record);

    try {
      await commitData('attendance', updated);
      showToast(remote ? 'Checked in (Remote)' : 'Checked in (Office)');
    } catch (e) {
      showToast(e.message || 'Could not save check-in', 'error');
    }
  };

  const checkOut = async () => {
    if (!user?.employeeId) {
      showToast('Employee profile not found for this account', 'error');
      return;
    }

    const employeeId = user.employeeId;
    const employee = user.name || 'Employee';

    const existing = (data.attendance || []).find(
      (a) => String(a.employeeId) === String(employeeId) && String(a.date) === String(date)
    );

    if (!existing) {
      showToast('Check in first to enable check-out', 'error');
      return;
    }

    let location = existing.location || 'Office';
    let remote = location === 'Remote';
    let status = existing.status || 'Present';

    // Re-evaluate location on check-out if geo verification is enabled.
    if (geoSettings?.enabled) {
      const officeLat = toNum(geoSettings.lat);
      const officeLng = toNum(geoSettings.lng);
      const radiusMeters = toNum(geoSettings.radius);
      if (officeLat != null && officeLng != null && radiusMeters != null) {
        try {
          const pos = await getCurrentGeolocation();
          const distance = haversineMeters(officeLat, officeLng, pos.coords.latitude, pos.coords.longitude);
          remote = distance > radiusMeters;
          location = remote ? 'Remote' : 'Office';
          status = remote && geoSettings?.enforceOnRemote ? 'Absent' : 'Present';
        } catch {
          // keep existing location/status if we can't read position
        }
      }
    }

    const now = new Date();
    const updated = (data.attendance || []).map((a) =>
      String(a.id) === String(existing.id)
        ? {
            ...a,
            employee,
            location,
            status,
            checkOut: formatHHMM(now),
          }
        : a
    );

    try {
      await commitData('attendance', updated);
      showToast(remote ? 'Checked out (Remote)' : 'Checked out (Office)');
    } catch (e) {
      showToast(e.message || 'Could not save check-out', 'error');
    }
  };

  const submitLeave = async () => {
    if (!user?.employeeId) {
      showToast('Employee profile not found for this account', 'error');
      return;
    }
    if (!leaveForm.from || !leaveForm.to || !leaveForm.reason.trim()) {
      showToast('Please complete all leave fields', 'error');
      return;
    }
    if (leaveForm.to < leaveForm.from) {
      showToast('End date must be on or after start date', 'error');
      return;
    }
    setLeaveSaving(true);
    try {
      const id = `LR-${Date.now()}`;
      const newReq = {
        id,
        employeeId: String(user.employeeId),
        employee: user.name || 'Employee',
        type: leaveForm.type,
        from: leaveForm.from,
        to: leaveForm.to,
        reason: leaveForm.reason.trim(),
        status: 'Pending',
        submittedAt: new Date().toISOString(),
      };
      await commitData('leaveRequests', [...(data.leaveRequests || []), newReq]);
      showToast('Leave request submitted — HR and you will be emailed if mail is configured');
      setShowLeaveModal(false);
      setLeaveForm({ type: 'Annual Leave', from: '', to: '', reason: '' });
    } catch (e) {
      showToast(e.message || 'Could not submit leave request', 'error');
    } finally {
      setLeaveSaving(false);
    }
  };

  const approvLeave = async (id) => {
    try {
      await commitData('leaveRequests', data.leaveRequests.map(l=>l.id===id?{...l,status:'Approved'}:l));
      showToast('Leave approved — employee will be notified by email');
    } catch {}
  };
  const rejectLeave = async (id) => {
    try {
      await commitData('leaveRequests', data.leaveRequests.map(l=>l.id===id?{...l,status:'Rejected'}:l));
      showToast('Leave rejected — employee will be notified by email','warning');
    } catch {}
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div><h1 className="page-title">Attendance</h1><p className="page-subtitle">Geo-location based tracking</p></div>
        <div className="page-actions">
          {!canViewAll && (
            <>
              <button className="btn-outline" onClick={checkIn}><Clock size={14}/> Check In</button>
              <button className="btn-ghost" onClick={checkOut}><Clock size={14}/> Check Out</button>
              <button className="btn-gold" onClick={() => setShowLeaveModal(true)}><Calendar size={14}/> Request Leave</button>
            </>
          )}
          {canViewAll && (
            <>
              <input className="form-input" type="date" value={date} onChange={e=>setDate(e.target.value)} style={{ width:'auto' }}/>
              <button className="btn-ghost" onClick={()=>showToast('Attendance report exported')}><Download size={14}/> Export</button>
            </>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="stats-row">
        {[
          { label:'Present',  value:present, color:'var(--success)', icon:CheckCircle },
          { label:'Absent',   value:absent,  color:'var(--danger)',  icon:XCircle },
          { label:'On Leave', value:onLeave, color:'var(--warning)', icon:AlertCircle },
        ].map(s=>(
          <div key={s.label} className="stat-card">
            <div className="stat-top"><div className="stat-icon" style={{ background:`${s.color}15`,color:s.color }}><s.icon size={18}/></div></div>
            <div className="stat-value" style={{ color:s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="info-banner info-banner-blue" style={{ marginBottom:20 }}>
        <MapPin size={15}/>
        <span>
          {geoSettings?.enabled
            ? `Geo-verification active — ${geoSettings.address} (±${geoSettings.radius}m radius). Remote check-ins are flagged automatically.`
            : 'Geo-verification disabled in System Settings.'}
        </span>
      </div>

      <div className={canViewAll ? 'two-col' : ''}>
        {/* Attendance table */}
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Employee</th><th>Check In</th><th>Check Out</th><th>Hours</th><th>Location</th><th>Status</th></tr></thead>
            <tbody>
              {records.map(a=>{
                const hrs = a.checkIn!=='-'&&a.checkOut!=='-' ? (() => {
                  const [ih,im]=a.checkIn.split(':').map(Number);
                  const [oh,om]=a.checkOut.split(':').map(Number);
                  const d=(oh*60+om)-(ih*60+im);
                  return `${Math.floor(d/60)}h ${d%60}m`;
                })() : '—';
                return (
                  <tr key={a.id}>
                    <td>
                      <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                        <div className="av av-sm av-gold">{a.employee.split(' ').map(n=>n[0]).join('').slice(0,2)}</div>
                        <span style={{ fontWeight:600,fontSize:'0.875rem' }}>{a.employee}</span>
                      </div>
                    </td>
                    <td><span style={{ fontFamily:'var(--font-mono)',fontSize:'0.85rem' }}>{a.checkIn}</span></td>
                    <td><span style={{ fontFamily:'var(--font-mono)',fontSize:'0.85rem',color:'var(--text-muted)' }}>{a.checkOut}</span></td>
                    <td><span style={{ fontFamily:'var(--font-mono)',fontWeight:700,color:a.status==='Present'?'var(--secondary-color)':'var(--text-dim)' }}>{hrs}</span></td>
                    <td>
                      {a.location!=='-'&&<div style={{ display:'flex',alignItems:'center',gap:4 }}><MapPin size={12} style={{ color:a.location==='Office'?'var(--success)':'var(--warning)' }}/><span style={{ fontSize:'0.8rem',color:'var(--text-muted)' }}>{a.location}</span></div>}
                      {a.location==='-'&&<span style={{ color:'var(--text-dim)' }}>—</span>}
                    </td>
                    <td><span className={`badge ${a.status==='Present'?'badge-success':a.status==='Absent'?'badge-danger':'badge-warning'}`}>{a.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Leave requests panel (HR/Admin/Founder only) */}
        {canViewAll && (
          <div className="card">
            <p className="card-title" style={{ marginBottom:14 }}>Leave Requests</p>
            {data.leaveRequests.map(l=>(
              <div key={l.id} style={{ padding:'12px 0',borderBottom:'1px solid var(--border-color)' }}>
                <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6 }}>
                  <p style={{ fontWeight:700,fontSize:'0.875rem' }}>{l.employee}</p>
                  <span className={`badge ${l.status==='Approved'?'badge-success':l.status==='Pending'?'badge-warning':'badge-danger'}`}>{l.status}</span>
                </div>
                <p style={{ fontSize:'0.78rem',color:'var(--text-muted)',marginBottom:6 }}>{l.type} · {l.from} → {l.to}</p>
                <p style={{ fontSize:'0.78rem',color:'var(--text-muted)',marginBottom:8,fontStyle:'italic' }}>"{l.reason}"</p>
                {l.status==='Pending' && (
                  <div style={{ display:'flex',gap:8 }}>
                    <button className="btn-gold" style={{ flex:1,padding:'6px',fontSize:'0.75rem' }} onClick={()=>approvLeave(l.id)}>Approve</button>
                    <button className="btn-ghost" style={{ flex:1,padding:'6px',fontSize:'0.75rem',color:'var(--danger)' }} onClick={()=>rejectLeave(l.id)}>Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!canViewAll && myLeaveRequests.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <p className="card-title" style={{ marginBottom: 14 }}>My leave requests</p>
          {myLeaveRequests.map((l) => (
            <div key={l.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <p style={{ fontWeight: 700, fontSize: '0.875rem' }}>{l.type}</p>
                <span className={`badge ${l.status === 'Approved' ? 'badge-success' : l.status === 'Pending' ? 'badge-warning' : 'badge-danger'}`}>{l.status}</span>
              </div>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{l.from} → {l.to}</p>
            </div>
          ))}
        </div>
      )}

      {showLeaveModal && (
        <div className="modal-overlay" onClick={() => !leaveSaving && setShowLeaveModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <p className="modal-title">Request leave</p>
              <button type="button" className="modal-close" onClick={() => setShowLeaveModal(false)} disabled={leaveSaving}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Leave type</label>
                <select className="form-input" value={leaveForm.type} onChange={(e) => setLeaveForm({ ...leaveForm, type: e.target.value })}>
                  {['Annual Leave', 'Sick Leave', 'Family Responsibility', 'Unpaid Leave'].map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">From</label>
                  <input className="form-input" type="date" value={leaveForm.from} onChange={(e) => setLeaveForm({ ...leaveForm, from: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">To</label>
                  <input className="form-input" type="date" value={leaveForm.to} onChange={(e) => setLeaveForm({ ...leaveForm, to: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Reason</label>
                <textarea className="form-input" rows={3} value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} placeholder="Brief reason for your request" />
              </div>
              <div className="info-banner info-banner-blue" style={{ marginBottom: 14 }}>
                <AlertCircle size={14}/>
                <span>HR and founders are notified by email when Cloud Functions mail settings (MAIL_USER, MAIL_PASS) are configured.</span>
              </div>
              <button type="button" className="btn-gold" style={{ width: '100%' }} onClick={submitLeave} disabled={leaveSaving}>
                {leaveSaving ? 'Submitting…' : 'Submit request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
